import * as yup from "yup";
import { StringConditions, DropdownConditions, DateConditions, BooleanConditions, NumberConditions } from "./DemoTypes";
import { DateCondition } from "../queries/getCrmRequests";

const StringFilter = yup
  .object()
  .shape({
    stringCondition: yup.mixed().oneOf(StringConditions).required("Please select a condition"),
    jsonValue: yup
      .array()
      .of(yup.string())
      .when("stringCondition", (stringCondition, schema) => {
        if (stringCondition === "Empty" || stringCondition === "NotEmpty") {
          return schema;
        }
        return schema.min(1, "At least one value is required");
      }),
  })
  .required();

const NumberFilter = yup.object().shape({
  numberCondition: yup.mixed().oneOf(NumberConditions).required("Please select a condition"),
  numberValue: yup
    .object()
    .when("numberCondition", (numberCondition, schema) => {
      if (numberCondition === "Empty" || numberCondition === "NotEmpty") {
        return schema;
      }
      if (numberCondition === "Between") {
        return schema.shape({
          firstNumber: yup.number().required("First value is required"),
          secondNumber: yup.number().required("Second value is required"),
        });
      }
      return schema.shape({
        firstNumber: yup.number().required("Value is required"),
        secondNumber: yup.number().nullable(),
      });
    })
    .required(),
});

const DateFilter = yup
  .object()
  .shape({
    dateCondition: yup.mixed().oneOf(DateConditions).required("Please select a condition"),
    dateValue: yup
      .object()
      .when("dateCondition", (dateCondition, schema) => {
        if (dateCondition === "Empty" || dateCondition === "NotEmpty") {
          return schema;
        }
        return schema.shape({
          startDate: yup.date().nullable(),
          endDate: yup.date().nullable(),
          dateOption: yup.string().nullable(),
        });
      })
      .required(),
  })
  .required()
  .test(
    "dateValue",
    "Must have at least one date or date option selected",
    (value: {
      dateCondition: DateCondition;
      dateValue: {
        startDate: Date | null | undefined;
        endDate: Date | null | undefined;
        dateOption: string | null | undefined;
      };
    }) => {
      const { dateValue, dateCondition } = value;
      if (dateCondition === "Empty" || dateCondition === "NotEmpty") {
        return true;
      }
      if (!dateValue) {
        return false;
      }
      if (!dateValue.startDate && !dateValue.endDate) {
        return !!dateValue.dateOption;
      }
      if (!dateValue.dateOption) {
        return !!dateValue.startDate || !!dateValue.endDate;
      }
      return true;
    },
  );

const BooleanFilter = yup
  .object()
  .shape({
    booleanCondition: yup.mixed().oneOf(BooleanConditions).required("Please select a condition"),
    booleanValue: yup.boolean().required(),
  })
  .required();

const DropdownFilter = yup
  .object()
  .shape({
    dropdownCondition: yup.mixed().oneOf(DropdownConditions).required("Please select a condition"),
    jsonValue: yup
      .array()
      .of(yup.string())
      .when("dropdownCondition", (dropdownCondition, schema) => {
        if (dropdownCondition === "Empty" || dropdownCondition === "NotEmpty") {
          return schema;
        }
        return schema.min(1, "At least one value is required");
      }),
  })
  .required();

export const FilterResolver = yup.object().shape({
  type: yup.string(),
  filter: yup
    .object()
    .when("type", {
      is: "Text",
      then: StringFilter,
    })
    .when("type", {
      is: "TextArea",
      then: StringFilter,
    })
    .when("type", {
      is: "Email",
      then: StringFilter,
    })
    .when("type", {
      is: "PhoneNumber",
      then: StringFilter,
    })
    .when("type", {
      is: "Address",
      then: StringFilter,
    })
    .when("type", {
      is: "URL",
      then: StringFilter,
    })
    .when("type", {
      is: "Checkbox",
      then: BooleanFilter,
    })
    .when("type", {
      is: "Number",
      then: NumberFilter,
    })
    .when("type", {
      is: "Currency",
      then: NumberFilter,
    })
    .when("type", {
      is: "Percent",
      then: NumberFilter,
    })
    .when("type", {
      is: "Date",
      then: DateFilter,
    })
    .when("type", {
      is: "DateTime",
      then: DateFilter,
    })
    .when("type", {
      is: "Time",
      then: DateFilter,
    })
    .when("type", {
      is: "Dropdown",
      then: DropdownFilter,
    })
    .when("type", {
      is: "DropdownMultiSelect",
      then: DropdownFilter,
    })
    .when("type", {
      is: "LookUp",
      then: DropdownFilter,
    })
    .required(),
});
